import { withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from "react";
import { useTranslation, withTranslation } from 'react-i18next';
import { removePrescriptionAPI } from '../../../api/medicalRecord';
import { AuditModal, CreateAudit, CreateAuditModal } from "../../../components/AuditTrail/AuditModal";
import { IconSVG } from '../../../components/Icons/Icons';
import { PrescriptionsForm } from "../../../components/MedicalRecords/Forms/PrescriptionsForm";
import { Prescriptions } from '../../../components/MedicalRecords/Prescriptions';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Success } from '../../../components/Notifications/Notifications';
import { ModalSpinner } from '../../../components/Utils/Loaders';
import { getMedicalRecordsSchema } from '../../../components/Validations/FormValidationSchema';
import { interopSystemEnum } from '../../../variables/Enums';
import { AuditTrail } from "../../Admin/AuditTrail";
import { useDrug, useDrugs } from "../Hooks/useDrugs";
import { usePrescription, usePrescriptions } from '../Hooks/usePrescriptions';
import { ModalData } from '../Modals/ModalData';

export const PrescriptionsContainer = (props) => {
    const [modal, setModal] = useState(null);
    const { entryStatus, entryId, medicalRecordNumber, showAddButton } = props;
    const [prescriptions, query, totalSize, isLoading, handleTableChange, setReload] = usePrescriptions(medicalRecordNumber, null);
    const [, , , , , print, recreate] = usePrescription();
    const { t } = useTranslation();

    const handleAdd = () => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.prescriptions.title`)}
            >
                <PrescriptionsCreateContainer {...props} hide={handleClose} onSubmit={handleSubmit} />
            </ModalData>
        );
    }

    const handleEdit = (id) => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.prescriptions.title`)}
            >
                <PrescriptionEditContainer
                    medicalRecordNumber={medicalRecordNumber}
                    entryId={entryId}
                    entryStatus={entryStatus}
                    id={id}
                    patient={props.patient}
                    onSubmit={handleSubmit}
                    onCancel={handleClose}
                />
            </ModalData>);
    }

    const handleCopy = async (_entryId2, id) => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.prescriptions.title`)}
            >
                <PrescriptionCopyContainer
                    medicalRecordNumber={medicalRecordNumber}
                    entryId={entryId}
                    entryStatus={entryStatus}
                    id={id}
                    patient={props.patient}
                    onSubmit={handleSubmit}
                    onCancel={handleClose}
                />
            </ModalData>);
    }

    const handleSubmit = () => {
        setModal(null);
        setReload(true);
        //if (props.onSubmit)
        //    props.onSubmit("prescriptions")
    }

    const handleClose = () => {
        setModal(null);
    }

    const handleAuditTrail = (entityId) => {
        setModal(
            <AuditModal
                onClose={() => { setModal(null); }}
                title={t("auditTrail.audit")}
            >
                <AuditTrail entity="PrescriptionsItems" entityId={entityId} medicalRecordNumber={medicalRecordNumber} />
            </AuditModal>
        )
    }

    const handlePrint = async (entryId2, id) => {
        try {
            var response = await print(medicalRecordNumber, entryId2, id, null);
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            HandleApiError(error);
            console.log(error);
        };
    }

    const handleRecreateExternal = async (entryId2, prescriptionId) => {
        try {
            await recreate(medicalRecordNumber, entryId2, prescriptionId);
            setReload(true);
        }
        catch (error) {
            HandleApiError(error);
            console.log(error);
        };
    }

    //#region Delete item with audit
    const handleRemove = (id) => {
        setModal(
            <ModalConfirm
                onConfirm={() => confirmRemoveItem(id)}
                onCancel={() => setModal(null)}
                title={t("medicalRecordsNotifications.item_delete_title")}
                description={t("medicalRecordsNotifications.item_delete_description")}
                confirmStyle="danger"
            />);
    }
    const confirmRemoveItem = (id) => {
        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => _removeItemAudited(id, reason, comments)}
            />)
        }
        else {
            _removeItemAudited(id, null, null);
        }
    }
    const _removeItemAudited = async (id, reason, comments) => {
        try {
            setModal(<ModalSpinner isShowing={true} hide={null} />);
            await removePrescriptionAPI(entryId, id, reason?.code, comments);
            setReload(true);
            setModal(null);
            Success("medicalRecordsNotifications.item_DeletedSuccessfully");
        }
        catch (error) {
            setModal(null);
            console.log(error);
            HandleApiError(error);
        }
    }
    //#endregion

    return (
        <>
            {modal}
            {
                showAddButton &&
                <div style={{ marginTop: '20px' }}>
                    <button key="buttonprescription" name="prescriptions" type="button"
                        className="btn btn-primary btn-fill btn-full"
                        onClick={() => handleAdd("prescriptions")}
                        style={{ marginBottom: '5px', textAlign: 'left' }}>
                        <><IconSVG name="prescriptions" fill="#fff" /> {t("commons.add")} {t("medicalRecords.prescriptions.title")}</>
                    </button>
                </div>
            }
            <Prescriptions
                withCard={false}
                entryId={entryId}
                data={prescriptions}
                query={query}
                totalSize={totalSize}
                isLoading={isLoading}
                onTableChange={handleTableChange}
                onEdit={handleEdit}
                onAuditTrail={handleAuditTrail}
                onPrint={handlePrint}
                onCopy={handleCopy}
                onRecreateExternal={handleRecreateExternal}
                onRemove={handleRemove}
            />
        </>
    );
}

export const PrescriptionsCreateContainer = (props) => {
    const { entryStatus, entryId, medicalRecordNumber, onSubmit } = props;
    const [, masterData, isLoadingMasterData] = useDrug(medicalRecordNumber, entryId);
    const [drugs, , , isLoadingDrugs] = useDrugs(medicalRecordNumber, entryId, true);
    const [, , , create] = usePrescription(medicalRecordNumber);
    const [modal, setModal] = useState(null);

    const handleSubmit = async (data) => {
        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setModal(<CreateAudit
                onClose={() => setModal(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (reason) {
                data.auditReason = reason;
                data.auditComments = comments;
            }
            data.prescriptions.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
            await create(entryId, data);
            setModal(null);
            onSubmit("prescriptions")
        }
        catch (error) {
            console.log(error);
            HandleApiError(error);
            props.hide()
        }
    }

    const handleCancel = () => {
        props.hide()
    }

    if (!masterData || isLoadingMasterData || isLoadingDrugs)
        return null;

    return (
        <>
            {
                modal ??
                <PrescriptionContainerFormik
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    data={masterData.data}
                    events={masterData.events}
                    drugs={drugs.filter(x => x.drug?.interopSystem === interopSystemEnum.VADEMECUM)}
                    patient={props.patient}
                />
            }
        </>

    );
}

const PrescriptionCopyContainer = (props) => {
    const { medicalRecordNumber, entryId, entryStatus, id, onSubmit, onCancel } = props;
    const [data, masterData, isLoading, create] = usePrescription(medicalRecordNumber, entryId, id);
    const [content, setContent] = useState(null);

    const handleSubmit = async (data) => {

        if (!data || !data.prescriptions || data.prescriptions.length === 0)
            return;

        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setContent(<CreateAudit
                onClose={() => setContent(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (data && data.prescriptions && data.prescriptions.length > 0) {
                if (reason) {
                    data.auditReason = reason;
                    data.auditComments = comments;
                }
                data.prescriptions.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
                //console.log(data)
                await create(entryId, data);
                setContent(null);
                onSubmit("prescriptions");
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    if (!data || !masterData || isLoading)
        return null;

    data.prescriptionDate = new Date();
    return (
        <>
            {content}
            <PrescriptionContainerFormik
                onSubmit={handleSubmit}
                onCancel={onCancel}
                data={masterData.data}
                events={masterData.events}
                prescriptions={data}
                patient={props.patient}
            />
        </>
    );
}

const PrescriptionEditContainer = (props) => {
    const { medicalRecordNumber, entryId, entryStatus, id, onSubmit } = props;
    const [data, masterData, isLoading, , update] = usePrescription(medicalRecordNumber, entryId, id);
    const [content, setContent] = useState(null);

    const handleSubmit = async (data) => {

        if (!data || !data.prescriptions || data.prescriptions.length === 0)
            return;

        if (entryStatus === "Published" || entryStatus === "AutoPublished" || entryId != data.prescriptions[0].entryId) {
            setContent(<CreateAudit
                onClose={() => setContent(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (data && data.prescriptions && data.prescriptions.length > 0) {
                if (reason) {
                    data.auditReason = reason;
                    data.auditComments = comments;
                }
                data.prescriptions.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
                await update(entryId, id, data)
                onSubmit();
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    if (!data || !masterData || isLoading)
        return null;

    return (
        <>
            {
                content ||
                <PrescriptionContainerFormik
                    onSubmit={handleSubmit}
                    onCancel={props.onCancel}
                    data={masterData.data}
                    events={masterData.events}
                    prescriptions={data}
                    patient={props.patient}
                />
            }
        </>
    );
}

const getFormattedData = (props, data) => {
    return data?.map((drug) => {
        if (typeof drug.frequency === 'string') {
            drug.unit = props.data.units.filter(x => x.title == drug.unit)[0];
            drug.frequency = props.data.frequencies.filter(x => x.title == drug.frequency)[0];
        }
        drug.drug = drug.name;
        drug.posology = drug.dose;
        drug.dose = null;
        drug.notes = `${drug.posology ?? ''} ${drug.unit?.description ?? ''} ${drug.frequency?.description ?? ''}`;
        return drug;
    });
}

const PrescriptionContainerFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => ({
        prescriptionDate: props.prescriptions?.prescriptionDate ?? new Date(),
        medicalInsurance: props.prescriptions?.medicalInsurance ?? props.patient?.medicalInsurance,
        medicalInsurancePlan: props.prescriptions?.medicalInsurancePlan ?? props.patient?.medicalInsurancePlan,
        medicalInsuranceNumber: props.prescriptions?.medicalInsuranceNumber ?? props.patient?.medicalInsuranceNumber,
        justification: props.prescriptions?.justification,
        type: props.prescriptions?.type ?? 0,
        prescriptions: props.prescriptions?.prescriptions ?? getFormattedData(props, JSON.parse(JSON.stringify(props.drugs)))
    }),

    validationSchema: getMedicalRecordsSchema().prescriptionsEdit,

    handleSubmit: (values, formikBag) => {
        formikBag.props.onSubmit(values);
    },

    displayName: 'PrescriptionsForm',

})(PrescriptionsForm));

PrescriptionContainerFormik.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    drugs: PropTypes.array,
    data: PropTypes.object
};